/**
 * Admin Feature - Data Fetching Hooks
 *
 * All read-only hooks use TanStack React Query (useQuery) backed by
 * queryOptions factories in yoyQueryOptions.ts and adminQueryOptions.ts.
 *
 * Mutation/transaction hooks (useExportTransactions, useRestoreAndExport,
 * useAddMatter) use manual
 * useState/useEffect as they involve optimistic updates or expose setters.
 */

import { useState, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { useApi } from "@/shared/utils/request";
import {
  yoySummaryOptions,
  yoyTimeSeriesOptions,
  dailyViewOptions,
  goLiveDatesOptions,
  professionalsOptions,
} from "./yoyQueryOptions";
import {
  usageMetricsOptions,
  exportStatsOptions,
  feedbackDataOptions,
  dashboardMetricsOptions,
  availableWeeksOptions,
  timekeeperPerformanceOptions,
  utilizationOptions,
  experimentalOptions,
} from "./adminQueryOptions";
import { logger } from "@/shared/utils/logger";
import { toast } from "sonner";
import {
  type EntryIdInput,
  formatEntryId,
} from "@/v2/hooks/actions/entryId";
import type { Professional, UsageRecord, FeedbackItem } from "../utils/adminHelpers";
import type {
  AttorneyExportStats,
  ExportStatsTotals,
  YoYSummary,
  YoYDataPoint,
  YoYGranularity,
  GoLiveRecord,
  AvailableWeek,
  PerformanceData,
  UtilizationData,
  DailyViewData,
  ExperimentalData,
} from "../types/dashboard";

/**
 * Stable empty results.
 *
 * Writing `query.data ?? []` inline allocates a fresh array on every render
 * for as long as the query is loading. Consumers memoize on that identity, so
 * a new object each render invalidates every derived value below it and the
 * caching never catches.
 *
 * That is what froze Admin > Entries: professionals fed
 * timekeepers -> profIds -> fieldOptions -> filters -> entriesScope and
 * tableColumns, which fed the entries query and the table, which scheduled
 * another render, which produced another array. The rest of these do not
 * currently close such a loop — they only recompute needlessly — but they are
 * the same shape, so they share one stable instance each. Frozen because a
 * single instance is shared by every caller.
 */
const EMPTY_PROFESSIONALS = Object.freeze([]) as unknown as Professional[];
const EMPTY_USAGE_RECORDS = Object.freeze([]) as unknown as UsageRecord[];
const EMPTY_EXPORT_TIMES = Object.freeze({}) as Record<string, string | null>;
const EMPTY_EXPORT_STATS = Object.freeze([]) as unknown as AttorneyExportStats[];
const EMPTY_FEEDBACK = Object.freeze([]) as unknown as FeedbackItem[];
const EMPTY_YOY_POINTS = Object.freeze([]) as unknown as YoYDataPoint[];
const EMPTY_GO_LIVE_RECORDS = Object.freeze([]) as unknown as GoLiveRecord[];
const EMPTY_AVAILABLE_WEEKS = Object.freeze([]) as unknown as AvailableWeek[];

/** Legacy `/admin/calendar-summary` row retained for reporting consumers. */
export interface AdminCalendarSummaryDay {
  date: string;
  unexportedHours: number;
  exportedHours: number;
  unexportedCount: number;
  exportedCount: number;
}

// ─────────────────────────────────────────────
// useProfessionals Hook
// ─────────────────────────────────────────────

export interface UseProfessionalsOptions {
  /** Only return professionals who have tracked time in Hourglass */
  activeOnly?: boolean;
}

export interface UseProfessionalsResult {
  professionals: Professional[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Fetch list of professionals (attorneys) for the firm.
 * Filters out test accounts.
 *
 * @param options.activeOnly - If true, only returns professionals with tracked time
 */
export function useProfessionals(
  options: UseProfessionalsOptions = {}
): UseProfessionalsResult {
  const { activeOnly = false } = options;
  const { makeApiRequest } = useApi();
  const query = useQuery(professionalsOptions(activeOnly, makeApiRequest));

  return {
    professionals: query.data ?? EMPTY_PROFESSIONALS,
    loading: query.isLoading,
    error: query.error?.message ?? null,
    refetch: async () => { await query.refetch(); },
  };
}

// ─────────────────────────────────────────────
// useUsageMetrics Hook
// ─────────────────────────────────────────────

export interface UseUsageMetricsResult {
  usageData: UsageRecord[];
  globalExportTimes: Record<string, string | null>;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Fetch usage metrics for the specified timeframe.
 * Returns usage records and global export times.
 */
export function useUsageMetrics(timeframe: string): UseUsageMetricsResult {
  const { makeApiRequest } = useApi();
  const query = useQuery(usageMetricsOptions(timeframe, makeApiRequest));

  return {
    usageData: query.data?.usageData ?? EMPTY_USAGE_RECORDS,
    globalExportTimes: query.data?.globalExportTimes ?? EMPTY_EXPORT_TIMES,
    loading: query.isLoading,
    error: query.error?.message ?? null,
    refetch: async () => { await query.refetch(); },
  };
}

// ─────────────────────────────────────────────
// useExportStats Hook
// ─────────────────────────────────────────────

export interface UseExportStatsResult {
  attorneys: AttorneyExportStats[];
  totals: ExportStatsTotals | null;
  loading: boolean;
  isPlaceholderData: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Fetch combined export statistics for all attorneys.
 * Returns attorney-level stats with exported/unexported hours and percentages.
 */
export function useExportStats(timeframe: string): UseExportStatsResult {
  const { makeApiRequest } = useApi();
  const query = useQuery(exportStatsOptions(timeframe, makeApiRequest));

  return {
    attorneys: query.data?.attorneys ?? EMPTY_EXPORT_STATS,
    totals: query.data?.totals ?? null,
    loading: query.isLoading,
    isPlaceholderData: query.isPlaceholderData,
    error: query.error?.message ?? null,
    refetch: async () => { await query.refetch(); },
  };
}

// ─────────────────────────────────────────────
// useFeedbackData Hook
// ─────────────────────────────────────────────

export interface UseFeedbackDataResult {
  feedbackData: FeedbackItem[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Fetch all feedback submissions for the support inbox.
 */
export function useFeedbackData(): UseFeedbackDataResult {
  const { makeApiRequest } = useApi();
  const query = useQuery(feedbackDataOptions(makeApiRequest));

  return {
    feedbackData: query.data ?? EMPTY_FEEDBACK,
    loading: query.isLoading,
    error: query.error?.message ?? null,
    refetch: async () => { await query.refetch(); },
  };
}

// ─────────────────────────────────────────────
// Export Transaction Helpers
// ─────────────────────────────────────────────

export interface ExportPreviewResult {
  total: number;
  count: number;
  attorneys: number;
}

/**
 * Hook for handling CSV export functionality.
 */
export function useExportTransactions() {
  const { makeApiRequest } = useApi();
  const [loading, setLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewResult, setPreviewResult] = useState<ExportPreviewResult | null>(
    null
  );
  const [error, setError] = useState<string | null>(null);

  const clearPreview = useCallback(() => {
    setPreviewResult(null);
    setError(null);
  }, []);

  const previewHours = useCallback(
    async (
      startDate: string,
      endDate: string,
      profIds: string[]
    ): Promise<ExportPreviewResult | null> => {
      setPreviewLoading(true);
      setPreviewResult(null);
      setError(null);

      try {
        const response = await makeApiRequest({
          endpoint: "/admin/exportCSV",
          method: "POST",
          body: { startDate, endDate, profIds },
        });

        const data = response.data || [];

        if (data.length === 0) {
          setError("No exported entries found for the selected criteria.");
          return null;
        }

        // Calculate total hours from Duration field
        const totalHours = data.reduce(
          (sum: number, row: Record<string, unknown>) => {
            const duration = parseFloat(String(row.Duration || 0));
            return sum + (Number.isNaN(duration) ? 0 : duration);
          },
          0
        );

        // Count unique attorneys
        const uniqueAttorneys = new Set(
          data.map(
            (row: Record<string, unknown>) =>
              row.Attorney || row.Professional || row.prof_id
          )
        ).size;

        const result = {
          total: Math.round(totalHours * 100) / 100,
          count: data.length,
          attorneys: uniqueAttorneys,
        };

        setPreviewResult(result);
        return result;
      } catch (e) {
        const errorMsg = "Failed to fetch preview data";
        logger.error(`${errorMsg}: ${e}`);
        toast.error(errorMsg, { duration: 5000 });
        return null;
      } finally {
        setPreviewLoading(false);
      }
    },
    [makeApiRequest]
  );

  const exportToCsv = useCallback(
    async (
      startDate: string,
      endDate: string,
      profIds: string[]
    ): Promise<boolean> => {
      setLoading(true);
      setError(null);

      try {
        const response = await makeApiRequest({
          endpoint: "/admin/exportCSV",
          method: "POST",
          body: { startDate, endDate, profIds },
        });

        const data = response.data || [];

        if (data.length === 0) {
          setError("No exported entries found for the selected criteria.");
          return false;
        }

        // Generate CSV
        const headers = [
          "Attorney",
          "MatterNo",
          "Duration",
          "Date",
          "WorkType",
          "Rate",
          "Description",
          "IsNoCharge",
          "IsNcds",
          "TaskCode",
          "ActivityCode",
        ];

        const csvRows = [
          headers.join(","),
          ...data.map((row: Record<string, unknown>) =>
            headers
              .map((header) => {
                const value = row[header];
                if (value === null || value === undefined) return "";
                const str = String(value);
                if (str.includes(",") || str.includes('"') || str.includes("\n")) {
                  return `"${str.replace(/"/g, '""')}"`;
                }
                return str;
              })
              .join(",")
          ),
        ];

        const csvContent = csvRows.join("\n");
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `exported_transactions_${startDate}_to_${endDate}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        toast.success("CSV exported", {
          description: `${data.length} records downloaded`,
        });
        return true;
      } catch (e) {
        logger.error(`Failed to export data: ${e}`);
        toast.error("Failed to export CSV", {
          duration: 5000,
          description: e instanceof Error ? e.message : undefined,
        });
        return false;
      } finally {
        setLoading(false);
      }
    },
    [makeApiRequest]
  );

  return {
    loading,
    previewLoading,
    previewResult,
    error,
    clearPreview,
    previewHours,
    exportToCsv,
  };
}

// ─────────────────────────────────────────────
// useDashboardMetrics Hook
// ─────────────────────────────────────────────

export interface DashboardMetrics {
  monthName: string;
  unexportedHours: number;
  exportedHours: number;
  deletedHours: number;
  activeUsers: number;
}

export interface UseDashboardMetricsResult {
  metrics: DashboardMetrics | null;
  loading: boolean;
  isPlaceholderData: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Fetch dashboard metrics for a date range.
 * If startDate/endDate are provided, filters to that range.
 * Otherwise defaults to current month (backend default).
 */
export function useDashboardMetrics(startDate?: string, endDate?: string): UseDashboardMetricsResult {
  const { makeApiRequest } = useApi();
  const query = useQuery(dashboardMetricsOptions(startDate, endDate, makeApiRequest));

  return {
    metrics: query.data ?? null,
    loading: query.isLoading,
    isPlaceholderData: query.isPlaceholderData,
    error: query.error?.message ?? null,
    refetch: async () => { await query.refetch(); },
  };
}

// ─────────────────────────────────────────────
// useRestoreAndExport Hook
// ─────────────────────────────────────────────

export interface UseRestoreAndExportResult {
  restoreAndExport: (id: EntryIdInput) => Promise<boolean>;
}

/**
 * Hook for restoring a deleted entry and exporting it.
 * The v2 export endpoint accepts deleted entries, restores them for export,
 * and re-archives them if export fails.
 * Loading state is managed by the caller to cover the full operation including any post-action refetch.
 */
export function useRestoreAndExport(): UseRestoreAndExportResult {
  const { makeApiRequest } = useApi();

  const restoreAndExport = useCallback(
    async (id: EntryIdInput): Promise<boolean> => {
      const toastId = toast.loading("Restoring and exporting entry...");
      try {
        const exportResp = await makeApiRequest({
          endpoint: "/v2/entries/export",
          method: "POST",
          body: { ids: [formatEntryId(id)] },
        });

        if (exportResp.error) {
          toast.error("Entry could not be exported", {
            id: toastId,
            duration: 5000,
            description: String(exportResp.error),
          });
          return true;
        }
        const { failedEntries: exportFailed = [] } = exportResp.data ?? {};
        if (exportFailed.length > 0) {
          toast.error("Entry could not be exported", {
            id: toastId,
            duration: 5000,
            description: exportFailed[0].error,
          });
          return true;
        }

        const expData = exportResp.data;
        if (expData?.success === false) {
          toast.error("Entry could not be exported", {
            id: toastId,
            duration: 5000,
            description: expData.errorMessage || "Export failed",
          });
          return true;
        }

        toast.success("Entry restored and exported", { id: toastId });
        return true;
      } catch (e) {
        const errorMsg =
          e instanceof Error ? e.message : "Failed to restore entry";
        logger.error(`Restore and export failed: ${e}`);
        toast.error("Failed to restore entry", {
          id: toastId,
          duration: 5000,
          description: errorMsg,
        });
        return false;
      }
    },
    [makeApiRequest]
  );

  return { restoreAndExport };
}

// ─────────────────────────────────────────────
// useDailyView Hook
// ─────────────────────────────────────────────

export interface UseDailyViewResult {
  data: DailyViewData | null;
  loading: boolean;
  isFetching: boolean;
  isPlaceholderData: boolean;
  error: string | null;
}

/**
 * Fetch per-attorney daily billing hours for the daily overlay chart.
 * Returns after (since go-live) and before (1 year prior) daily arrays.
 * Requires a specific profId — not available for firm-wide view.
 * Query is disabled (no fetch) when profId is null.
 */
export function useDailyView(profId: string | null): UseDailyViewResult {
  const { makeApiRequest } = useApi();
  const query = useQuery(dailyViewOptions(profId, makeApiRequest));

  return {
    data: query.data ?? null,
    loading: query.isLoading,
    isFetching: query.isFetching,
    isPlaceholderData: query.isPlaceholderData,
    error: query.error?.message ?? null,
  };
}

// ─────────────────────────────────────────────
// Add Matter Hook
// ─────────────────────────────────────────────

export function useAddMatter() {
  const { makeApiRequest } = useApi();
  const [loading, setLoading] = useState(false);

  const addMatter = useCallback(
    async (values: {
      matter_id: string;
      client: string;
      description: string;
      matter: string;
    }): Promise<{ success: boolean; error?: string }> => {
      setLoading(true);

      try {
        const resp = await makeApiRequest({
          endpoint: "/matters/add",
          method: "POST",
          body: values,
        });

        if (resp.error) {
          const errorMsg = String(resp.error);
          if (errorMsg.includes("already exists") || resp.status === "ERROR") {
            return {
              success: false,
              error: "This Matter ID already exists. Please use a different ID.",
            };
          }
          return { success: false, error: errorMsg || "Failed to add matter" };
        }

        toast.success("Matter added");
        return { success: true };
      } catch (e) {
        logger.error(`Failed to add matter: ${e}`);
        toast.error("Failed to add matter", {
          duration: 5000,
          description: e instanceof Error ? e.message : undefined,
        });
        return { success: false, error: e instanceof Error ? e.message : "Failed to add matter" };
      } finally {
        setLoading(false);
      }
    },
    [makeApiRequest]
  );

  return { loading, addMatter };
}

// ─────────────────────────────────────────────
// useYoYSummary Hook
// ─────────────────────────────────────────────

export interface UseYoYSummaryResult {
  summary: YoYSummary | null;
  loading: boolean;
  isFetching: boolean;
  isPlaceholderData: boolean;
  error: string | null;
}

/**
 * Fetch go-live-anchored billing summary (total billed, hours, averages).
 * Before/After windows are computed server-side from each attorney's
 * Hourglass go-live date — no date params needed.
 */
export function useYoYSummary(
  profId: string | null
): UseYoYSummaryResult {
  const { makeApiRequest } = useApi();
  const query = useQuery(yoySummaryOptions(profId, makeApiRequest));

  return {
    summary: query.data ?? null,
    loading: query.isLoading,
    isFetching: query.isFetching,
    isPlaceholderData: query.isPlaceholderData,
    error: query.error?.message ?? null,
  };
}

// ─────────────────────────────────────────────
// useYoYTimeSeries Hook
// ─────────────────────────────────────────────

export interface UseYoYTimeSeriesResult {
  data: YoYDataPoint[];
  loading: boolean;
  isFetching: boolean;
  isPlaceholderData: boolean;
  error: string | null;
}

/**
 * Fetch go-live-anchored time series breakdown (weekly or monthly).
 * Returns data points grouped by period, year, and professional.
 */
export function useYoYTimeSeries(
  granularity: YoYGranularity,
  profId: string | null
): UseYoYTimeSeriesResult {
  const { makeApiRequest } = useApi();
  const query = useQuery(yoyTimeSeriesOptions(granularity, profId, makeApiRequest));

  return {
    data: query.data ?? EMPTY_YOY_POINTS,
    loading: query.isLoading,
    isFetching: query.isFetching,
    isPlaceholderData: query.isPlaceholderData,
    error: query.error?.message ?? null,
  };
}

// ─────────────────────────────────────────────
// useGoLiveDates Hook
// ─────────────────────────────────────────────

export interface UseGoLiveDatesResult {
  goLiveDates: GoLiveRecord[];
  loading: boolean;
  error: string | null;
}

/**
 * Fetch per-attorney Hourglass go-live dates.
 * Go-live = MIN(date) from transactions UNION deleted_transactions.
 */
export function useGoLiveDates(): UseGoLiveDatesResult {
  const { makeApiRequest } = useApi();
  const query = useQuery(goLiveDatesOptions(makeApiRequest));

  return {
    goLiveDates: query.data ?? EMPTY_GO_LIVE_RECORDS,
    loading: query.isLoading,
    error: query.error?.message ?? null,
  };
}

// ─────────────────────────────────────────────
// useAvailableWeeks Hook
// ─────────────────────────────────────────────

export interface UseAvailableWeeksResult {
  weeks: AvailableWeek[];
  loading: boolean;
  error: string | null;
}

/**
 * Fetch distinct weeks that have billing data, newest first.
 * Used by the Timekeeper Performance tab's week navigator.
 */
export function useAvailableWeeks(): UseAvailableWeeksResult {
  const { makeApiRequest } = useApi();
  const query = useQuery(availableWeeksOptions(makeApiRequest));

  return {
    weeks: query.data ?? EMPTY_AVAILABLE_WEEKS,
    loading: query.isLoading,
    error: query.error?.message ?? null,
  };
}

// ─────────────────────────────────────────────
// useTimekeeperPerformance Hook
// ─────────────────────────────────────────────

export interface UseTimekeeperPerformanceResult {
  performance: PerformanceData | null;
  loading: boolean;
  isPlaceholderData: boolean;
  error: string | null;
}

/**
 * Fetch rolling performance metrics for all timekeepers as of a given week end date.
 * Returns current week/month/YTD hours plus averages and prior-year comparisons.
 */
export function useTimekeeperPerformance(
  asOfDate: string | null
): UseTimekeeperPerformanceResult {
  const { makeApiRequest } = useApi();
  const query = useQuery(timekeeperPerformanceOptions(asOfDate, makeApiRequest));

  return {
    performance: query.data ?? null,
    loading: query.isLoading,
    isPlaceholderData: query.isPlaceholderData,
    error: query.error?.message ?? null,
  };
}

// ─────────────────────────────────────────────
// useUtilization Hook
// ─────────────────────────────────────────────

export interface UseUtilizationResult {
  utilization: UtilizationData | null;
  loading: boolean;
  isPlaceholderData: boolean;
  error: string | null;
}

/**
 * Fetch Hourglass utilization vs total billing hours per attorney.
 * utilization_pct = hourglass_exported_hours / total_billing_hours * 100
 *
 * Reusable across tabs — any component can call this with its own date range.
 */
export function useUtilization(
  startDate: string,
  endDate: string
): UseUtilizationResult {
  const { makeApiRequest } = useApi();
  const query = useQuery(utilizationOptions(startDate, endDate, makeApiRequest));

  return {
    utilization: query.data ?? null,
    loading: query.isLoading,
    isPlaceholderData: query.isPlaceholderData,
    error: query.error?.message ?? null,
  };
}

// ─────────────────────────────────────────────
// useExperimental Hook
// ─────────────────────────────────────────────

export interface UseExperimentalResult {
  data: ExperimentalData | null;
  loading: boolean;
  error: string | null;
}

/**
 * Fetch experimental weekly hours trend for all licensed professionals.
 * Returns pre-HG weekly average and weekly billed hours since go-live per attorney.
 */
export function useExperimental(): UseExperimentalResult {
  const { makeApiRequest } = useApi();
  const query = useQuery(experimentalOptions(makeApiRequest));

  return {
    data: query.data ?? null,
    loading: query.isLoading,
    error: query.error?.message ?? null,
  };
}
